import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Card,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Grid,
  Paper,
  Button,
  IconButton,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import axios from 'axios';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { UserListHead } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';
import { sortLanes } from '../utils/lanes';
import { matchesSearch } from '../utils/search';
import { CALIBER_OPTIONS } from '../utils/calibers';
import { isPrivateCourse, COURSE_LANGUAGE_OPTIONS, LICENSE_TYPE_OPTIONS, privateCourseSummary } from '../utils/privateCourse';

const db = getFirestore(firebaseApp);

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

const TABLE_HEAD = [
  { id: 'startTime', label: 'Time', alignRight: false },
  { id: 'userName', label: 'Customer', alignRight: false },
  { id: 'categoryName', label: 'Category', alignRight: false },
  { id: 'laneName', label: 'Lane', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'confirmationCode', label: 'Code', alignRight: false },
  { id: 'amountCents', label: 'Amount', alignRight: false },
  { id: '', label: '', alignRight: false },
];

const statusColors = {
  confirmed: 'success',
  pending_payment: 'warning',
  completed: 'info',
  cancelled: 'default',
  refunded: 'secondary',
  expired: 'default',
  no_show: 'error',
};

// Cliente marcado "Showed" en el detalle → verde en el calendario (pedido
// 2026-09-15), sin importar el status (confirmada, completada...).
const showedColors = { bg: '#22C55E', text: '#FFF' };
// Bloqueos de lane (agencia / evento): gris oscuro y con el TÍTULO del
// bloqueo (ej. "Cualificación") en vez de "Admin" (pedido 2026-09-15).
const blockColors = { bg: '#454F5B', text: '#FFF' };
// Si se reservó "para otra persona", la reservación es a nombre de ESA
// persona (pedido 2026-09-30); quien la hizo se muestra aparte.
const displayName = (r) => {
  if (r.isBlock) return r.blockReason || r.categoryName || 'Bloqueo';
  if (r.reservationFor && r.reservationFor.name) return r.reservationFor.name;
  return r.userName;
};
const bookedByName = (r) => (
  !r.isBlock && r.reservationFor && r.reservationFor.name && r.userName ? r.userName : null
);

const statusBgColors = {
  confirmed: { bg: '#1877F2', text: '#FFF' },
  pending_payment: { bg: '#FFAB00', text: '#FFF' },
  completed: { bg: '#637381', text: '#FFF' },
  cancelled: { bg: '#DFE3E8', text: '#637381' },
  refunded: { bg: '#DFE3E8', text: '#637381' },
  no_show: { bg: '#FF5630', text: '#FFF' },
  expired: { bg: '#DFE3E8', text: '#637381' },
};

const laneAvatarColors = [
  '#1877F2', '#d40100', '#FFAB00', '#22C55E', '#8E33FF',
  '#00B8D9', '#FF5630', '#637381', '#FF6C40', '#36B37E',
];

const HOUR_HEIGHT = 64;

export default function ReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  // Vista compacta (estilo Amelia): todo el día visible sin scroll horizontal
  const [compact, setCompact] = useState(() => localStorage.getItem('bbtcCalCompact') !== '0');
  const toggleCompact = () => setCompact((c) => {
    localStorage.setItem('bbtcCalCompact', c ? '0' : '1');
    return !c;
  });
  const laneMinW = compact ? 60 : 160;
  const gutterW = compact ? 48 : 80;
  const hourH = compact ? 40 : HOUR_HEIGHT;
  const navigate = useNavigate();

  // Hora actual (minutos desde medianoche), refrescada cada minuto, para la
  // línea roja "ahora" del calendario (estilo Amelia).
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  useEffect(() => {
    const tick = () => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); };
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // El item "Pagos" del menú abre esta página directo en la vista de pagos
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('view') === 'payments') setViewMode('payments');
  }, [searchParams]);

  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLane, setFilterLane] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Search by confirmation code
  const [searchCode, setSearchCode] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearchByCode = async () => {
    const code = searchCode.trim().toUpperCase();
    if (!code) return;
    setSearchLoading(true);
    setSearchError('');
    try {
      const q = query(collection(db, 'reservations'), where('confirmationCode', '==', code));
      const snap = await getDocs(q);
      if (snap.empty) {
        setSearchError(`No reservation found with code "${code}"`);
      } else {
        const docId = snap.docs[0].id;
        navigate(`details?id=${docId}`);
      }
    } catch (e) {
      console.error('Search error:', e);
      setSearchError('Error searching. Try again.');
    }
    setSearchLoading(false);
  };

  // Create reservation dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [createForm, setCreateForm] = useState({
    customer: null,
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    licenseNumber: '',
    licenseExpiration: '',
    categoryId: '',
    laneId: '',
    date: '',
    startTime: '',
    notes: '',
    calibers: [],
    otherCaliber: '',
    courseLanguage: '',
    licenseType: '',
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate, filterCategory, filterStatus]);

  const fetchMetadata = async () => {
    const [catsSnap, lanesSnap, membersSnap] = await Promise.all([
      getDocs(collection(db, 'reservation_categories')),
      getDocs(collection(db, 'lanes')),
      getDocs(collection(db, 'members')),
    ]);
    setCategories(catsSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
    setLanes(lanesSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
    setMembers(membersSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
  };

  // Cierres de lanes por curso (lane_closures) — el lane está físicamente
  // ocupado por el curso, así que se pinta cerrado en el calendario.
  const [laneClosures, setLaneClosures] = useState([]);

  useEffect(() => {
    const loadClosures = async () => {
      try {
        const dow = new Date(`${selectedDate}T12:00:00`).getDay();
        const snap = await getDocs(
          query(collection(db, 'lane_closures'), where('dayOfWeek', '==', dow), where('active', '==', true))
        );
        setLaneClosures(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
      } catch (e) {
        console.error('Error cargando cierres de lanes:', e);
        setLaneClosures([]);
      }
    };
    loadClosures();
  }, [selectedDate]);

  // Cierre que cubre este lane en esta hora (o null)
  const closureForCell = (laneId, hour) =>
    laneClosures.find((c) => {
      if (!(c.laneIds || []).includes(laneId)) return false;
      const cStart = timeToMinutes(c.startTime);
      const cEnd = timeToMinutes(c.endTime);
      return cStart < (hour + 1) * 60 && cEnd > hour * 60;
    }) || null;

  const openCreateDialog = (prefill = {}) => {
    setCreateError(null);
    setCreateForm({
      customer: null,
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      licenseNumber: '',
      categoryId: prefill.categoryId || '',
      laneId: prefill.laneId || '',
      date: prefill.date || selectedDate,
      startTime: prefill.startTime || '',
      notes: '',
      calibers: [],
      otherCaliber: '',
      courseLanguage: '',
      licenseType: '',
      sendPaymentLink: false,
    });
    setCreateOpen(true);
  };

  // ¿El cliente escrito a mano ya existe en members (por teléfono o email)?
  // Si no, es cliente nuevo y se le pide toda la información.
  const existingMatch = useMemo(() => {
    if (createForm.customer) return createForm.customer;
    const ph = createForm.customerPhone.replace(/\D/g, '');
    const em = createForm.customerEmail.trim().toLowerCase();
    if (!ph && !em) return null;
    return members.find((m) => (
      (ph && String(m.phone || '').replace(/\D/g, '') === ph)
      || (em && String(m.email || '').trim().toLowerCase() === em)
    )) || null;
  }, [createForm.customer, createForm.customerPhone, createForm.customerEmail, members]);
  const isNewClient = !createForm.customer && !existingMatch
    && Boolean(createForm.customerName.trim() || createForm.customerPhone.trim() || createForm.customerEmail.trim());

  const handleCreateReservation = async () => {
    if (!createForm.categoryId || !createForm.date || !createForm.startTime) {
      setCreateError('Categoría, fecha y hora son requeridos.');
      return;
    }
    // Registro de tiro: pistola/rifle requieren número de licencia
    const selectedCat = categories.find((c) => c.id === createForm.categoryId);
    if (selectedCat?.requiresLicense && !createForm.licenseNumber.trim()) {
      setCreateError(`${selectedCat.name} requiere el número de licencia de armas del cliente.`);
      return;
    }
    // Cliente NUEVO (no existe en members): se le crea la cuenta, así que se
    // pide toda la información requerida (pedido 2026-09-15).
    if (isNewClient) {
      if (!createForm.customerName.trim() || createForm.customerName.trim().split(/\s+/).length < 2) {
        setCreateError('Cliente nuevo: escribe el nombre y apellido completos.');
        return;
      }
      if (!createForm.customerPhone.trim() || createForm.customerPhone.replace(/\D/g, '').length < 10) {
        setCreateError('Cliente nuevo: el teléfono es requerido (10 dígitos).');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.customerEmail.trim())) {
        setCreateError('Cliente nuevo: el email es requerido y debe ser válido.');
        return;
      }
      if (selectedCat?.requiresLicense && !createForm.licenseExpiration) {
        setCreateError('Cliente nuevo: la fecha de expiración de la licencia de armas es requerida.');
        return;
      }
    } else if (!createForm.customer && !createForm.customerName.trim() && !createForm.customerPhone.trim() && !createForm.customerEmail.trim()) {
      setCreateError('Escoge un cliente existente o escribe los datos del cliente.');
      return;
    }
    // Registro de tiro: en categorías de tiro se anota el calibre que usará
    const asksCaliber = selectedCat && ['lane', 'no-license-shooting'].includes(selectedCat.type);
    const finalCalibers = [
      ...createForm.calibers,
      ...(createForm.otherCaliber.trim() ? [createForm.otherCaliber.trim()] : []),
    ];
    if (asksCaliber && finalCalibers.length === 0) {
      setCreateError('Selecciona el calibre (o calibres) que el cliente va a utilizar — es parte del registro del campo de tiro.');
      return;
    }
    // Curso privado: idioma y nueva/renovación son obligatorios
    const isPrivate = isPrivateCourse(selectedCat);
    if (isPrivate && (!createForm.courseLanguage || !createForm.licenseType)) {
      setCreateError('Para un curso privado indica el idioma (Español/Inglés) y si es licencia nueva o renovación.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const payload = {
        categoryId: createForm.categoryId,
        date: createForm.date,
        startTime: createForm.startTime,
        laneId: createForm.laneId || null,
        customerName: createForm.customer?.fullName || createForm.customerName,
        customerEmail: createForm.customer?.email || createForm.customerEmail,
        customerPhone: createForm.customer?.phone || createForm.customerPhone,
        licenseNumber: createForm.licenseNumber.trim() || null,
        licenseExpiration: createForm.licenseExpiration || null,
        calibers: finalCalibers,
        courseLanguage: isPrivate ? createForm.courseLanguage : null,
        licenseType: isPrivate ? createForm.licenseType : null,
        notes: createForm.notes,
        createdBy: 'admin',
        sendPaymentLink: createForm.sendPaymentLink === true,
      };
      if (payload.sendPaymentLink && !(payload.customerEmail || '').trim() && !(payload.customerPhone || '').trim()) {
        setCreateError('Para enviar el link de pago el cliente necesita email o teléfono.');
        setCreateSaving(false);
        return;
      }
      const r = await fetch(`${FUNCTIONS_BASE}/adminCreateReservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok) {
        setCreateOpen(false);
        fetchReservations();
        if (data.paymentLink) {
          const pl = data.paymentLink;
          const sent = [pl.emailSent ? 'email' : null, pl.smsSent ? 'SMS' : null].filter(Boolean).join(' y ');
          alert(sent
            ? `Reservación pendiente de pago. Link enviado por ${sent}.\n\n${pl.url}`
            : `Reservación pendiente de pago, pero NO se pudo enviar el link (${pl.smsError || pl.emailError || 'sin email ni teléfono'}). Cópialo y envíaselo al cliente:\n\n${pl.url}`);
        } else if (createForm.sendPaymentLink && Number(data.amountCents) === 0) {
          alert('No se envió link de pago: el precio para este cliente es $0 (miembro). La reservación quedó confirmada.');
        }
      } else {
        setCreateError(data.error || 'No se pudo crear la reservación.');
      }
    } catch (e) {
      setCreateError('Error de red.');
    }
    setCreateSaving(false);
  };

  const lanesForSelectedCategory = useMemo(() => {
    if (!createForm.categoryId) return lanes;
    return lanes.filter((l) => Array.isArray(l.categoryIds) && l.categoryIds.includes(createForm.categoryId));
  }, [createForm.categoryId, lanes]);

  // Cargar slots disponibles cuando cambia categoría/fecha/lane
  useEffect(() => {
    if (!createOpen) {
      return undefined;
    }
    if (!createForm.categoryId || !createForm.date) {
      setAvailableSlots([]);
      return undefined;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setAvailableSlots([]);
    (async () => {
      try {
        const params = {
          categoryId: createForm.categoryId,
          date: createForm.date,
          includePast: true, // admin puede asignar walk-ins en horas actuales/pasadas del día
        };
        if (createForm.laneId) params.laneId = createForm.laneId;
        const r = await fetch(`${FUNCTIONS_BASE}/getAvailableSlots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });
        const data = await r.json();
        if (!cancelled && data.slots) setAvailableSlots(data.slots);
      } catch (e) {
        if (!cancelled) setAvailableSlots([]);
      }
      if (!cancelled) setSlotsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [createOpen, createForm.categoryId, createForm.date, createForm.laneId]);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (filterCategory) params.categoryId = filterCategory;
      if (filterStatus) params.status = filterStatus;

      const response = await axios.post(`${FUNCTIONS_BASE}/getReservationsForDate`, params);
      let data = response.data.reservations || [];
      if (filterLane) {
        data = data.filter((r) => r.laneId === filterLane);
      }
      setReservations(data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
    setLoading(false);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const filteredReservations = filterLane
    ? reservations.filter((r) => r.laneId === filterLane)
    : reservations;

  // Lanes agrupados por categoría (Pistola juntos 1..n, luego Rifle 1..n, etc.)
  const activeLanes = useMemo(
    () => sortLanes(lanes.filter((l) => l.active), categories),
    [lanes, categories]
  );

  const dateObj = new Date(`${selectedDate}T12:00:00`);
  const dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const changeDate = (days) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const goToToday = () => setSelectedDate(today);

  const datePickerRef = useRef(null);
  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (e) { /* fallback below */ }
    }
    el.focus();
    el.click();
  };

  // Los cursos no se muestran en este calendario — viven en Cursos → Calendario.
  // (Sí salen en la vista de lista.)
  const calendarReservations = useMemo(
    () => reservations.filter((r) => r.categoryType !== 'course'),
    [reservations]
  );

  // Calendar: compute time range from availability or default 8-20
  const calendarHours = useMemo(() => {
    let minH = 8;
    let maxH = 20;
    calendarReservations.forEach((r) => {
      if (r.startTime) {
        const h = parseInt(r.startTime.split(':')[0], 10);
        if (h < minH) minH = h;
      }
      if (r.endTime) {
        const h = parseInt(r.endTime.split(':')[0], 10);
        if (h > maxH) maxH = h;
      }
    });
    const hours = [];
    for (let h = minH; h <= maxH; h += 1) {
      hours.push(h);
    }
    return hours;
  }, [calendarReservations]);

  const timeToMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };

  const calendarStartMin = calendarHours.length > 0 ? calendarHours[0] * 60 : 480;

  const getReservationsForLane = (laneId) =>
    calendarReservations.filter((r) => r.laneId === laneId && ['confirmed', 'pending_payment', 'completed', 'no_show'].includes(r.status));

  const getUnassignedReservations = () =>
    calendarReservations.filter((r) => !r.laneId && ['confirmed', 'pending_payment', 'completed', 'no_show'].includes(r.status));

  // Una reservación ocupa TODOS los slots que cruza, no solo el de su hora de
  // inicio: una reserva 18:00–20:00 se ve en la fila de 6 PM y en la de 7 PM.
  // Así el empleado no ve la hora siguiente vacía y la vuelve a vender.
  const reservationSpansHour = (r, hour) => {
    const rStart = timeToMinutes(r.startTime);
    const rEnd = r.endTime ? timeToMinutes(r.endTime) : rStart + (Number(r.durationMinutes) || 60);
    return rStart < (hour + 1) * 60 && rEnd > hour * 60;
  };

  // ---- Pagos ----
  const monthStartISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const [payStart, setPayStart] = useState(monthStartISO());
  const [payEnd, setPayEnd] = useState(new Date().toISOString().split('T')[0]);
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (viewMode !== 'payments') return;
    (async () => {
      setPayLoading(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'reservations'),
          where('date', '>=', payStart),
          where('date', '<=', payEnd),
        ));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => (p.amountCents || 0) > 0 || p.stripePaymentIntentId)
          .sort((a, b) => (b.date + (b.startTime || '')).localeCompare(a.date + (a.startTime || '')));
        setPayments(list);
      } catch (e) { console.error(e); }
      setPayLoading(false);
    })();
  }, [viewMode, payStart, payEnd]);

  const payTotals = useMemo(() => {
    const sum = (fn) => payments.filter(fn).reduce((s, p) => s + (p.amountCents || 0), 0);
    return {
      collected: sum((p) => p.paymentStatus === 'succeeded'),
      refunded: sum((p) => p.paymentStatus === 'refunded'),
      pending: sum((p) => ['pending', 'pending_payment'].includes(p.paymentStatus) || p.status === 'pending_payment'),
      count: payments.filter((p) => p.paymentStatus === 'succeeded').length,
    };
  }, [payments]);

  const payStatusChip = (p) => {
    const map = {
      succeeded: { label: 'Pagado', color: 'success' },
      refunded: { label: 'Reembolsado', color: 'secondary' },
      pending: { label: 'Pendiente', color: 'warning' },
      free: { label: 'Gratis', color: 'default' },
      recurring: { label: 'Recurrente', color: 'info' },
      admin_created: { label: 'Admin', color: 'default' },
      not_required: { label: 'N/A', color: 'default' },
    };
    const c = map[p.paymentStatus] || { label: p.paymentStatus || '—', color: 'default' };
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const getCategoryNamesForLane = (lane) => {
    if (!Array.isArray(lane.categoryIds) || lane.categoryIds.length === 0) return '';
    return lane.categoryIds
      .map((cId) => categories.find((c) => c.id === cId))
      .filter(Boolean)
      .map((c) => c.name)
      .join(', ');
  };

  // Color de fondo configurado en la categoría (ej. Rifle, Tiro sin licencia).
  // Si el lane tiene varias categorías, se usa la primera que tenga color.
  const getLaneColor = (lane) => {
    if (!Array.isArray(lane.categoryIds)) return '';
    for (let i = 0; i < lane.categoryIds.length; i += 1) {
      const c = categories.find((cat) => cat.id === lane.categoryIds[i]);
      if (c && c.calendarColor) return c.calendarColor;
    }
    return '';
  };

  const handleCellClick = (lane, hour) => {
    const startTime = `${hour.toString().padStart(2, '0')}:00`;
    const categoryId = Array.isArray(lane.categoryIds) && lane.categoryIds.length === 1 ? lane.categoryIds[0] : '';
    openCreateDialog({ laneId: lane.id, date: selectedDate, startTime, categoryId });
  };

  return (
    <>
      <Helmet>
        <title>Reservations | BBTC Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Reservations</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by code..."
              value={searchCode}
              onChange={(e) => { setSearchCode(e.target.value.toUpperCase()); setSearchError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchByCode()}
              error={!!searchError}
              helperText={searchError || undefined}
              InputProps={{
                endAdornment: searchLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <IconButton size="small" onClick={handleSearchByCode} disabled={!searchCode.trim()}>
                    <Iconify icon="eva:search-fill" />
                  </IconButton>
                ),
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={openCreateDialog}
            >
              Nueva reservación
            </Button>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
              <ToggleButton value="calendar"><Iconify icon="eva:calendar-fill" /></ToggleButton>
              <ToggleButton value="list"><Iconify icon="eva:list-fill" /></ToggleButton>
              <ToggleButton value="payments" title="Pagos"><Iconify icon="eva:credit-card-fill" /></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <>
            {/* Calendar toolbar */}
            <Card sx={{ p: 2, mb: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" size="small" onClick={goToToday} sx={{ minWidth: 60 }}>Today</Button>
                  <IconButton size="small" onClick={() => changeDate(-1)}><Iconify icon="eva:chevron-left-fill" /></IconButton>
                  <IconButton size="small" onClick={() => changeDate(1)}><Iconify icon="eva:chevron-right-fill" /></IconButton>
                  <Box
                    onClick={openDatePicker}
                    sx={{
                      ml: 1,
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{dateLabel}</Typography>
                    <Iconify icon="eva:calendar-outline" width={18} sx={{ color: 'text.secondary' }} />
                    <input
                      ref={datePickerRef}
                      type="date"
                      value={selectedDate}
                      onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '100%',
                        opacity: 0,
                        pointerEvents: 'none',
                        width: 1,
                        height: 1,
                      }}
                      tabIndex={-1}
                      aria-label="Cambiar fecha"
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel
                    control={<Switch size="small" checked={compact} onChange={toggleCompact} />}
                    label={<Typography variant="body2">Compacto</Typography>}
                  />
                  <TextField select size="small" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} sx={{ minWidth: 160 }} label="Category">
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((cat) => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }} label="Status">
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="pending_payment">Pending</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="no_show">No Show</MenuItem>
                  </TextField>
                </Stack>
              </Stack>
            </Card>

            {/* Lane pills */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 3, overflowX: 'auto', pb: 1 }} alignItems="center">
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#637381', width: 28, height: 28, fontSize: 12 }}>All</Avatar>}
                label="All Lanes"
                variant={!filterLane ? 'filled' : 'outlined'}
                onClick={() => setFilterLane('')}
                sx={!filterLane ? { bgcolor: alpha('#637381', 0.12), fontWeight: 600 } : {}}
              />
              {activeLanes.map((lane, idx) => {
                const color = laneAvatarColors[idx % laneAvatarColors.length];
                const count = getReservationsForLane(lane.id).length;
                return (
                  <Chip
                    key={lane.id}
                    avatar={<Avatar sx={{ bgcolor: color, width: 28, height: 28, fontSize: 12, color: '#FFF' }}>{count}</Avatar>}
                    label={getCategoryNamesForLane(lane) ? `${lane.name} · ${getCategoryNamesForLane(lane)}` : lane.name}
                    variant={filterLane === lane.id ? 'filled' : 'outlined'}
                    onClick={() => setFilterLane(filterLane === lane.id ? '' : lane.id)}
                    sx={filterLane === lane.id ? { bgcolor: alpha(color, 0.12), fontWeight: 600 } : {}}
                  />
                );
              })}
            </Stack>

            {/* Calendar grid */}
            <Card sx={{ overflow: 'hidden' }}>
              {loading ? (
                <Box sx={{ p: 3 }}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={HOUR_HEIGHT - 4} sx={{ mb: 0.5, borderRadius: 1 }} animation="wave" />
                  ))}
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: (filterLane ? 1 : activeLanes.length) * laneMinW + gutterW + 10, display: 'flex', flexDirection: 'column' }}>
                    {/* Column headers */}
                    <Box sx={{ display: 'flex', borderBottom: '2px solid', borderColor: 'divider', bgcolor: 'grey.50', position: 'sticky', top: 0, zIndex: 2 }}>
                      <Box sx={{ width: gutterW, flexShrink: 0, p: compact ? 0.75 : 1.5, borderRight: '1px solid', borderColor: 'divider' }} />
                      {(filterLane ? activeLanes.filter((l) => l.id === filterLane) : activeLanes).map((lane, idx) => (
                        <Box key={lane.id} sx={{ flex: 1, minWidth: laneMinW, p: compact ? 0.75 : 1.5, textAlign: 'center', borderRight: '1px solid', borderColor: 'divider', overflow: 'hidden', ...(getLaneColor(lane) ? { bgcolor: getLaneColor(lane), borderBottom: '3px solid', borderBottomColor: getLaneColor(lane) } : {}) }}>
                          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, fontSize: compact ? 12 : undefined }}>{lane.name}</Typography>
                          {getCategoryNamesForLane(lane) && (
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                display: 'block',
                                fontWeight: 700,
                                color: 'error.main',
                                textTransform: 'uppercase',
                                fontSize: compact ? 10 : 11.5,
                                lineHeight: 1.3,
                              }}
                            >
                              {getCategoryNamesForLane(lane)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? 10 : undefined }}>
                            {getReservationsForLane(lane.id).length}{compact ? '' : ' reservations'}
                          </Typography>
                        </Box>
                      ))}
                      {!filterLane && getUnassignedReservations().length > 0 && (
                        <Box sx={{ flex: 1, minWidth: laneMinW, p: compact ? 0.75 : 1.5, textAlign: 'center' }}>
                          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, fontSize: compact ? 12 : undefined }}>No Lane</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? 10 : undefined }}>
                            {getUnassignedReservations().length}{compact ? '' : ' reservations'}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Time rows */}
                    {calendarHours.map((hour) => {
                      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                      // Línea roja de la hora actual: solo hoy y solo en la fila
                      // de la hora en curso. Se posiciona en % de la fila para
                      // que siga exacta aunque la fila crezca por reservas.
                      const showNowLine = selectedDate === today && nowMin >= hour * 60 && nowMin < (hour + 1) * 60;
                      const nowPct = ((nowMin - hour * 60) / 60) * 100;
                      return (
                        <Box key={hour} sx={{ display: 'flex', minHeight: hourH, borderBottom: '1px solid', borderColor: 'divider', position: 'relative' }}>
                          {showNowLine && (
                            <Box
                              sx={{
                                position: 'absolute', left: 0, right: 0, top: `${nowPct}%`,
                                height: 0, borderTop: '2px solid #d40100', zIndex: 3, pointerEvents: 'none',
                                '&::before': {
                                  content: '""', position: 'absolute', left: gutterW - 6, top: -5,
                                  width: 10, height: 10, borderRadius: '50%', bgcolor: '#d40100',
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  position: 'absolute', left: 2, top: -9, px: 0.5, lineHeight: '16px',
                                  fontSize: 10, fontWeight: 700, color: '#FFF', bgcolor: '#d40100', borderRadius: 0.5,
                                }}
                              >
                                {formatTime(`${Math.floor(nowMin / 60).toString().padStart(2, '0')}:${(nowMin % 60).toString().padStart(2, '0')}`)}
                              </Typography>
                            </Box>
                          )}
                          {/* Time label */}
                          <Box sx={{ width: gutterW, flexShrink: 0, p: compact ? 0.5 : 1, borderRight: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? 10 : 12, fontWeight: 500, mt: -0.5 }}>
                              {formatTime(timeStr)}
                            </Typography>
                          </Box>

                          {/* Lane columns */}
                          {(filterLane ? activeLanes.filter((l) => l.id === filterLane) : activeLanes).map((lane) => {
                            const laneReservations = calendarReservations.filter(
                              (r) => r.laneId === lane.id && ['confirmed', 'pending_payment', 'completed', 'no_show'].includes(r.status)
                                && reservationSpansHour(r, hour)
                            );
                            const closure = closureForCell(lane.id, hour);
                            const cellFree = laneReservations.length === 0 && !closure;
                            const laneColor = getLaneColor(lane);
                            return (
                              <Box
                                key={lane.id}
                                onClick={() => { if (cellFree) handleCellClick(lane, hour); }}
                                title={closure ? `Cerrado por curso (${closure.startTime}–${closure.endTime})` : undefined}
                                sx={{
                                  flex: 1, minWidth: laneMinW, borderRight: '1px solid', borderColor: 'divider', p: compact ? 0.25 : 0.5, position: 'relative',
                                  cursor: cellFree ? 'pointer' : 'default',
                                  ...(laneColor ? { bgcolor: laneColor } : {}),
                                  '&:hover': cellFree ? (laneColor ? { filter: 'brightness(0.93)' } : { bgcolor: 'action.hover' }) : {},
                                  ...(closure && laneReservations.length === 0 ? {
                                    // Rayado diagonal = lane cerrado por un curso
                                    backgroundImage:
                                      'repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0 6px, transparent 6px 12px)',
                                  } : {}),
                                }}
                              >
                                {closure && laneReservations.length === 0 && !compact && (
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ display: 'block', color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}
                                  >
                                    Cerrado (curso)
                                  </Typography>
                                )}
                                {laneReservations.map((r) => {
                                  const colors = r.isBlock ? blockColors : (r.showed && r.status !== 'no_show' ? showedColors : (statusBgColors[r.status] || statusBgColors.confirmed));
                                  // Continuación = la reserva empezó en una hora
                                  // anterior. Se pinta igual pero atenuada y con
                                  // "↳" para que se lea como la misma reserva.
                                  const isContinuation = timeToMinutes(r.startTime) < hour * 60;
                                  return (
                                    <Box
                                      key={r.id}
                                      onClick={() => navigate(`details?id=${r.id}`)}
                                      title={`${displayName(r)} · ${formatTime(r.startTime)} - ${formatTime(r.endTime)}`}
                                      sx={{
                                        bgcolor: colors.bg,
                                        color: colors.text,
                                        borderRadius: compact ? 0.75 : 1.5,
                                        px: compact ? 0.75 : 1.5,
                                        py: compact ? 0.25 : 0.75,
                                        mb: compact ? 0.25 : 0.5,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        '&:hover': { opacity: 0.85, transform: 'scale(1.02)' },
                                        overflow: 'hidden',
                                        ...(isContinuation ? { opacity: 0.6 } : {}),
                                      }}
                                    >
                                      <Typography variant="caption" noWrap sx={{ fontWeight: 700, display: 'block', lineHeight: 1.3, fontSize: compact ? 10.5 : 12 }}>
                                        {isContinuation ? `↳ ${displayName(r)}` : displayName(r)}
                                      </Typography>
                                      {!compact && (
                                        <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.8, lineHeight: 1.2, fontSize: 11 }}>
                                          {formatTime(r.startTime)} - {formatTime(r.endTime)}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          })}

                          {/* Unassigned column */}
                          {!filterLane && getUnassignedReservations().length > 0 && (
                            <Box sx={{ flex: 1, minWidth: laneMinW, p: compact ? 0.25 : 0.5 }}>
                              {getUnassignedReservations().filter(
                                (r) => reservationSpansHour(r, hour)
                              ).map((r) => {
                                const colors = r.isBlock ? blockColors : (r.showed && r.status !== 'no_show' ? showedColors : (statusBgColors[r.status] || statusBgColors.confirmed));
                                const isContinuation = timeToMinutes(r.startTime) < hour * 60;
                                return (
                                  <Box
                                    key={r.id}
                                    onClick={() => navigate(`details?id=${r.id}`)}
                                    title={`${displayName(r)} · ${r.categoryName}${privateCourseSummary(r, categories.find((c) => c.id === r.categoryId)) ? ` · ${privateCourseSummary(r, categories.find((c) => c.id === r.categoryId))}` : ''}${r.partnerName ? ` · Aliado: ${r.partnerName}` : ''}`}
                                    sx={{
                                      bgcolor: colors.bg,
                                      color: colors.text,
                                      borderRadius: compact ? 0.75 : 1.5,
                                      px: compact ? 0.75 : 1.5,
                                      py: compact ? 0.25 : 0.75,
                                      mb: compact ? 0.25 : 0.5,
                                      cursor: 'pointer',
                                      '&:hover': { opacity: 0.85 },
                                      ...(isContinuation ? { opacity: 0.6 } : {}),
                                    }}
                                  >
                                    <Typography variant="caption" noWrap sx={{ fontWeight: 700, display: 'block', fontSize: compact ? 10.5 : 12 }}>
                                      {isContinuation ? `↳ ${displayName(r)}` : displayName(r)}
                                    </Typography>
                                    {!compact && (
                                      <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.8, fontSize: 11 }}>
                                        {r.categoryName}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Card>

            {/* Legend */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {Object.entries({ confirmed: 'Confirmed', pending_payment: 'Pending', completed: 'Completed', no_show: 'No Show', cancelled: 'Cancelled' }).map(([key, label]) => (
                <Stack key={key} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: statusBgColors[key].bg }} />
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Stack>
              ))}
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: blockColors.bg }} />
                <Typography variant="caption" color="text.secondary">Bloqueo (agencia/evento)</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: showedColors.bg }} />
                <Typography variant="caption" color="text.secondary">Showed (llegó)</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 0, borderTop: '2px solid #d40100' }} />
                <Typography variant="caption" color="text.secondary">Hora actual</Typography>
              </Stack>
            </Stack>
          </>
        )}

        {/* Payments View */}
        {viewMode === 'payments' && (
          <>
            <Card sx={{ p: 2, mb: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Pagos</Typography>
                <TextField
                  size="small"
                  type="date"
                  label="Desde"
                  value={payStart}
                  onChange={(e) => e.target.value && setPayStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Hasta"
                  value={payEnd}
                  onChange={(e) => e.target.value && setPayEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Card>

            {/* Analytics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Cobrado', value: `$${(payTotals.collected / 100).toFixed(2)}`, sub: `${payTotals.count} pagos`, color: 'success.main' },
                { label: 'Pendiente', value: `$${(payTotals.pending / 100).toFixed(2)}`, sub: 'por cobrar', color: 'warning.main' },
                { label: 'Reembolsado', value: `$${(payTotals.refunded / 100).toFixed(2)}`, sub: 'devuelto', color: 'text.secondary' },
                { label: 'Neto', value: `$${((payTotals.collected - payTotals.refunded) / 100).toFixed(2)}`, sub: 'cobrado − reembolsos', color: 'primary.main' },
              ].map((t) => (
                <Grid item xs={6} md={3} key={t.label}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                    <Typography variant="h5" sx={{ color: t.color, fontWeight: 700 }}>{t.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.sub}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Card>
              {payLoading ? (
                <Stack alignItems="center" sx={{ p: 4 }}><CircularProgress /></Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Categoría</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Monto</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Pago</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reservación</TableCell>
                        <TableCell />
                      </TableRow>
                      {payments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            No hay pagos en este rango de fechas.
                          </TableCell>
                        </TableRow>
                      )}
                      {payments.map((p) => (
                        <TableRow
                          key={p.id}
                          hover
                          onClick={() => navigate(`details?id=${p.id}`)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{p.date}{p.startTime ? ` · ${formatTime(p.startTime)}` : ''}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.userName}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.userEmail}</Typography>
                          </TableCell>
                          <TableCell>{p.categoryName}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>${((p.amountCents || 0) / 100).toFixed(2)}</TableCell>
                          <TableCell>{payStatusChip(p)}</TableCell>
                          <TableCell>
                            <Chip label={p.status} color={statusColors[p.status] || 'default'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.secondary' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Toca un pago para ver el detalle; ahí puedes reembolsar (Cancel &amp; Refund) o marcar pagado/no pagado.
            </Typography>
          </>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            <Card sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Date" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth select label="Category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((cat) => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth select label="Lane" value={filterLane} onChange={(e) => setFilterLane(e.target.value)}>
                    <MenuItem value="">All Lanes</MenuItem>
                    {lanes.map((lane) => <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="pending_payment">Pending Payment</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                    <MenuItem value="no_show">No Show</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Card>

            <Card>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 900 }}>
                  <Table>
                    <UserListHead headLabel={TABLE_HEAD} />
                    <TableBody>
                      {loading && <TableSkeleton rows={5} columns={8} />}
                      {!loading && filteredReservations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                        <TableRow
                          hover
                          key={row.id}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`details?id=${row.id}`)}
                        >
                          <TableCell padding="checkbox" />
                          <TableCell>{formatTime(row.startTime)} - {formatTime(row.endTime)}</TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">{displayName(row)}</Typography>
                            {bookedByName(row) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Reservó: {bookedByName(row)}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">{row.userEmail}</Typography>
                          </TableCell>
                          <TableCell>
                            {row.categoryName}
                            {privateCourseSummary(row, categories.find((c) => c.id === row.categoryId)) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {privateCourseSummary(row, categories.find((c) => c.id === row.categoryId))}
                              </Typography>
                            )}
                            {row.partnerName && (
                              <Chip size="small" label={`Aliado: ${row.partnerName}`} color="warning" variant="outlined" sx={{ mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>{row.laneName || '—'}</TableCell>
                          <TableCell>
                            {row.showed && row.status !== 'no_show' ? (
                              <Chip label={row.status === 'completed' ? 'completed · showed' : 'showed'} color="success" size="small" />
                            ) : (
                              <Chip label={row.status.replace('_', ' ')} color={statusColors[row.status] || 'default'} size="small" />
                            )}
                          </TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.confirmationCode}</Typography></TableCell>
                          <TableCell>${((row.amountCents || 0) / 100).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Iconify icon="eva:chevron-right-fill" />
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && filteredReservations.length === 0 && (
                        <TableRow>
                          <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                            <Typography variant="body2">No reservations found for this date</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredReservations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
              />
            </Card>
          </>
        )}
      </Container>

      {/* Create Reservation Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear reservación para cliente</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <Autocomplete
              options={members}
              openOnFocus
              getOptionLabel={(opt) => opt.fullName ? `${opt.fullName} — ${opt.email || opt.phone || ''}` : ''}
              value={createForm.customer}
              onChange={(e, v) => setCreateForm({
                ...createForm,
                customer: v,
                customerName: v ? (v.fullName || '') : '',
                customerEmail: v ? (v.email || '') : '',
                customerPhone: v ? (v.phone || '') : '',
                licenseNumber: v ? (v.licenseNumber || v.gunLicense || '') : '',
                licenseExpiration: '',
              })}
              renderInput={(params) => <TextField {...params} label="Buscar cliente existente" placeholder="Nombre, email o teléfono" />}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOptions={(options, state) => {
                // Ignora acentos y el orden de las palabras: "jose cruz"
                // encuentra "José E. Rodríguez Cruz" (pedido del cliente)
                const q = state.inputValue;
                if (!q.trim()) return options.slice(0, 50);
                return options.filter((o) => matchesSearch(q, [
                  o.fullName, o.email, o.phone, o.memberId,
                ])).slice(0, 50);
              }}
            />

            <Typography variant="caption" color="text.secondary">
              O ingresa los datos manualmente:
            </Typography>

            {isNewClient && (
              <Alert severity="info">
                <strong>Cliente nuevo</strong> — no existe en el sistema, se le creará su cuenta con esta reservación.
                Nombre y apellido, teléfono y email son requeridos
                {categories.find((c) => c.id === createForm.categoryId)?.requiresLicense ? ', más el número y la expiración de la licencia de armas' : ''}.
              </Alert>
            )}
            {!createForm.customer && existingMatch && (
              <Alert severity="success">
                Cliente existente: <strong>{existingMatch.fullName}</strong>{existingMatch.phone ? ` · ${existingMatch.phone}` : ''}{existingMatch.email ? ` · ${existingMatch.email}` : ''}. La reservación se le asignará a su cuenta.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={isNewClient ? 'Nombre y apellido *' : 'Nombre'}
                value={createForm.customerName}
                onChange={(e) => setCreateForm({ ...createForm, customerName: e.target.value })}
                fullWidth
                disabled={!!createForm.customer}
                error={isNewClient && createForm.customerName.trim().split(/\s+/).filter(Boolean).length < 2}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={isNewClient ? 'Email *' : 'Email'}
                value={createForm.customerEmail}
                onChange={(e) => setCreateForm({ ...createForm, customerEmail: e.target.value })}
                fullWidth
                disabled={!!createForm.customer}
                error={isNewClient && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.customerEmail.trim())}
              />
              <TextField
                label={isNewClient ? 'Teléfono *' : 'Teléfono'}
                value={createForm.customerPhone}
                onChange={(e) => setCreateForm({ ...createForm, customerPhone: e.target.value.replace(/\D/g, '') })}
                fullWidth
                disabled={!!createForm.customer}
                error={isNewClient && createForm.customerPhone.replace(/\D/g, '').length < 10}
                helperText="Solo números — el formato se añade automático al mostrarlo"
              />
            </Stack>

            {(() => {
              const cat = categories.find((c) => c.id === createForm.categoryId);
              const requires = cat?.requiresLicense === true;
              // El registro de tiro necesita la licencia: requerida en
              // pistola/rifle, opcional en el resto.
              return (
                <TextField
                  label={requires ? 'Número de licencia de armas (requerido)' : 'Número de licencia de armas (opcional)'}
                  value={createForm.licenseNumber}
                  onChange={(e) => setCreateForm({ ...createForm, licenseNumber: e.target.value })}
                  fullWidth
                  required={requires}
                  error={requires && !createForm.licenseNumber.trim()}
                  helperText={requires && !createForm.licenseNumber.trim() ? `${cat.name} requiere licencia para el registro de tiro` : ''}
                />
              );
            })()}

            {isNewClient && categories.find((c) => c.id === createForm.categoryId)?.requiresLicense && (
              <TextField
                type="date"
                label="Expiración de la licencia de armas *"
                value={createForm.licenseExpiration}
                onChange={(e) => setCreateForm({ ...createForm, licenseExpiration: e.target.value })}
                fullWidth
                required
                error={!createForm.licenseExpiration}
                InputLabelProps={{ shrink: true }}
                helperText="Se guarda en la cuenta nueva del cliente"
              />
            )}

            {(() => {
              const cat = categories.find((c) => c.id === createForm.categoryId);
              const asksCaliber = cat && ['lane', 'no-license-shooting'].includes(cat.type);
              if (!asksCaliber) return null;
              const toggleCaliber = (cal) => setCreateForm((prev) => ({
                ...prev,
                calibers: prev.calibers.includes(cal)
                  ? prev.calibers.filter((c) => c !== cal)
                  : [...prev.calibers, cal],
              }));
              return (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Calibre(s) que va a utilizar *</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Selecciona todos los que apliquen — es parte del registro del campo de tiro.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {CALIBER_OPTIONS.map((cal) => (
                      <Chip
                        key={cal}
                        label={cal}
                        clickable
                        onClick={() => toggleCaliber(cal)}
                        color={createForm.calibers.includes(cal) ? 'error' : 'default'}
                        variant={createForm.calibers.includes(cal) ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                  <TextField
                    label="Otro calibre"
                    value={createForm.otherCaliber}
                    onChange={(e) => setCreateForm({ ...createForm, otherCaliber: e.target.value })}
                    size="small"
                    sx={{ mt: 2 }}
                    placeholder="Ej: 10MM"
                  />
                </Box>
              );
            })()}

            {(() => {
              const cat = categories.find((c) => c.id === createForm.categoryId);
              if (!isPrivateCourse(cat)) return null;
              return (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Curso privado *</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Idioma y si es licencia nueva o renovación (define el pago al instructor).
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {COURSE_LANGUAGE_OPTIONS.map((o) => (
                      <Chip
                        key={o.value}
                        label={o.label}
                        clickable
                        onClick={() => setCreateForm((prev) => ({ ...prev, courseLanguage: o.value }))}
                        color={createForm.courseLanguage === o.value ? 'error' : 'default'}
                        variant={createForm.courseLanguage === o.value ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {LICENSE_TYPE_OPTIONS.map((o) => (
                      <Chip
                        key={o.value}
                        label={o.label}
                        clickable
                        onClick={() => setCreateForm((prev) => ({ ...prev, licenseType: o.value }))}
                        color={createForm.licenseType === o.value ? 'error' : 'default'}
                        variant={createForm.licenseType === o.value ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })()}

            {/* Link de pago (2026-10-07): solo para cursos inscritos por el staff */}
            {(() => {
              const cat = categories.find((c) => c.id === createForm.categoryId);
              if (!cat || !['course', 'class'].includes(cat.type)) return null;
              return (
                <Box sx={{ p: 1.5, border: '1px dashed', borderColor: createForm.sendPaymentLink ? 'warning.main' : 'divider', borderRadius: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={createForm.sendPaymentLink === true} onChange={(e) => setCreateForm({ ...createForm, sendPaymentLink: e.target.checked })} color="warning" />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enviar link de pago por SMS y email</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
                    {createForm.sendPaymentLink
                      ? 'La inscripción queda PENDIENTE hasta que el cliente pague por el link (precio de miembro o no-miembro + IVU). Al pagar, se confirma sola.'
                      : 'Apagado: queda confirmada y el pago se coordina en mostrador.'}
                  </Typography>
                </Box>
              );
            })()}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Categoría *</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {categories.filter((c) => c.active).map((c) => {
                  const selected = createForm.categoryId === c.id;
                  return (
                    <Box
                      key={c.id}
                      onClick={() => setCreateForm({ ...createForm, categoryId: c.id, laneId: '', startTime: '' })}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.75, borderRadius: 2, cursor: 'pointer',
                        border: '1.5px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.lighter' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary', bgcolor: selected ? 'primary.lighter' : 'action.hover' },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {c.durationMinutes} min · ${((c.priceNonMemberCents || 0) / 100).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <TextField
              select
              label="Lane (opcional)"
              value={createForm.laneId}
              onChange={(e) => setCreateForm({ ...createForm, laneId: e.target.value })}
              fullWidth
              helperText="Deja vacío para asignar al azar"
            >
              <MenuItem value="">Cualquiera disponible</MenuItem>
              {lanesForSelectedCategory.map((l) => (
                <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Fecha"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={createForm.date}
              onChange={(e) => setCreateForm({ ...createForm, date: e.target.value, startTime: '' })}
              fullWidth
              required
            />

            {createForm.categoryId && createForm.date && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Horas disponibles
                </Typography>
                {slotsLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Buscando horarios...</Typography>
                  </Stack>
                ) : availableSlots.length === 0 ? (
                  <Alert severity="warning">
                    No hay horarios disponibles para esta categoría{createForm.laneId ? ' y lane' : ''} en esta fecha.
                  </Alert>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {availableSlots.map((s) => {
                      const isSelected = createForm.startTime === s.startTime;
                      const disabled = s.available === false;
                      return (
                        <Button
                          key={s.startTime}
                          size="small"
                          disabled={disabled}
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => setCreateForm({ ...createForm, startTime: s.startTime })}
                          sx={{ minWidth: 90, borderRadius: 2 }}
                        >
                          {(() => {
                            const [h, m] = s.startTime.split(':');
                            const hour = parseInt(h, 10);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const h12 = hour % 12 || 12;
                            return `${h12}:${m} ${ampm}`;
                          })()}
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {(!createForm.categoryId || !createForm.date) && (
              <Typography variant="caption" color="text.secondary">
                Selecciona categoría y fecha para ver las horas disponibles.
              </Typography>
            )}

            <TextField
              label="Notas (opcional)"
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateReservation}
            disabled={createSaving}
            startIcon={createSaving ? <CircularProgress size={16} /> : null}
          >
            {createSaving ? 'Creando...' : 'Crear reservación'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
